import React, { useEffect, useState, useCallback } from "react"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import NewsletterCheckbox from "./newsletter_checkbox"
import { type Customer, ClientRole } from "@/api/graphql"
import { vatEntry, einvoiceEntry, shouldValidateVat } from "@/checkout/common"
import ClientData from "@/common/client_data"
import ClientRoleSwitch from "@/common/components/client_role_switch"
import FormCheckbox from "@/common/components/form_checkbox"
import FormInput from "@/common/components/form_input"
import FormSelect from "@/common/components/form_select"
import { streetNrRequired, showStreet2, stateRequired } from "@/common/locales"
import Util from "@/common/util"
import yup from "@/common/yup"

export interface CustomerFormInputs {
  [key: string]: string | boolean | undefined | null
  firstName: string
  lastName: string
  company?: string | null
  vatId?: string | null
  orgId?: string | null
  glnNumber?: string | null
  reference?: string | null
  street: string
  street2?: string | null
  houseNumber?: string | null
  zipcode: string
  city: string
  state?: string | null
  country: string
  phone1?: string | null
  invoiceEmail?: string | null
  differentDeliveryAddress: boolean
  deliveryFirstName?: string | null
  deliveryLastName?: string | null
  deliveryCompany?: string | null
  deliveryStreet?: string | null
  deliveryStreet2?: string | null
  deliveryHouseNumber?: string | null
  deliveryZipcode?: string | null
  deliveryCity?: string | null
  deliveryState?: string | null
  deliveryCountry?: string | null
  deliveryPhone?: string | null
  email?: string
  password?: string
  newsletterEnabled?: boolean
  clientRole?: ClientRole
}

export interface RegisterFormInputs extends CustomerFormInputs {
  email: string
  password: string
}

export const customerSchema = (register: boolean) => {
  return yup.object().shape<CustomerFormInputs>({
    email: register ? yup.string().required() : yup.string(),
    password: register ? yup.string().required().min(3, "customer.min_password_length") : yup.string(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    company: yup
      .string()
      .trim()
      .nullable()
      .when(["clientRole"], {
        is: clientRole => clientRole === ClientRole.business,
        then: yup
          .string()
          .trim()
          .notOneOf(Util.validations.forbiddenCompanies, "customer.invalid_company")
          .matches(/(?!^\d+$)^.+$/, { message: "customer.invalid_company", excludeEmptyString: true }),
      }),
    vatId: yup
      .string()
      .nullable()
      .when(["company", "deliveryCountry", "country"], {
        is: (company, deliveryCountry, country) =>
          vatEntry(deliveryCountry || country, company) && shouldValidateVat(company, deliveryCountry || country),
        then: yup.string().trim().vat("deliveryCountry"),
      }),
    orgId: yup
      .string()
      .nullable()
      .when(["company", "clientRole"], {
        is: (company, clientRole) => !Util.isBlank(company) && clientRole === "business",
        then: yup.string().trim().orgid("country"),
      }),
    glnNumber: yup
      .string()
      .nullable()
      .when(["company", "clientRole"], {
        is: (company, clientRole) => !Util.isBlank(company) && clientRole === "business",
        then: yup.string().trim().gln("country"),
      }),
    reference: yup.string().nullable(),
    street: yup.string().required(),
    street2: yup.string().nullable(),
    houseNumber: yup.string().nullable().when("country", {
      is: streetNrRequired,
      then: yup.string().required(),
    }),
    zipcode: yup.string().required(),
    state: yup
      .string()
      .nullable()
      .when("country", {
        is: (country: string) => stateRequired(country),
        then: yup.string().nullable().required(),
      }),
    city: yup.string().required(),
    country: yup
      .string()
      .required()
      .oneOf(ClientData.deliveryCountries?.map(c => c[1]) || [], "Invalid country"),
    differentDeliveryAddress: yup.bool().required(),
    phone1: yup
      .string()
      .nullable()
      .matches(Util.validations.phone, { message: "customer.phone_format", excludeEmptyString: true })
      .when("country", {
        is: country => country === "NO",
        then: yup
          .string()
          .nullable()
          .matches(/^\d{8}$/, { message: "customer.phone_invalid", excludeEmptyString: true }),
      })
      .when("differentDeliveryAddress", {
        is: false,
        then: yup
          .string()
          .required()
          .matches(Util.validations.phone, "customer.phone_format")
          .when("country", {
            is: country => country === "NO",
            then: yup.string().nullable().length(8, "customer.phone_invalid"),
          }),
      }),
    invoiceEmail: yup.string().nullable(),
    deliveryFirstName: yup.string().nullable().when("differentDeliveryAddress", {
      is: true,
      then: yup.string().required(),
    }),
    deliveryLastName: yup.string().nullable().when("differentDeliveryAddress", {
      is: true,
      then: yup.string().required(),
    }),
    deliveryCompany: yup
      .string()
      .nullable()
      .when("differentDeliveryAddress", {
        is: true,
        then: yup
          .string()
          .notOneOf(Util.validations.forbiddenCompanies, "customer.invalid_company")
          .matches(/(?!^\d+$)^.+$/, { message: "customer.invalid_company", excludeEmptyString: true }),
      }),
    deliveryStreet: yup.string().nullable().when("differentDeliveryAddress", {
      is: true,
      then: yup.string().required(),
    }),
    deliveryStreet2: yup.string().nullable(),
    deliveryZipcode: yup.string().nullable().when("differentDeliveryAddress", {
      is: true,
      then: yup.string().required(),
    }),
    deliveryState: yup
      .string()
      .nullable()
      .when("differentDeliveryAddress", {
        is: true,
        then: yup
          .string()
          .nullable()
          .when("deliveryCountry", {
            is: (country: string) => stateRequired(country),
            then: yup.string().nullable().required(),
          }),
      }),
    deliveryHouseNumber: yup
      .string()
      .nullable()
      .when("differentDeliveryAddress", {
        is: true,
        then: yup.string().when("deliveryCountry", {
          is: streetNrRequired,
          then: yup.string().required(),
        }),
      }),
    deliveryCity: yup.string().nullable().when("differentDeliveryAddress", {
      is: true,
      then: yup.string().required(),
    }),
    deliveryCountry: yup
      .string()
      .nullable()
      .when("differentDeliveryAddress", {
        is: true,
        then: yup
          .string()
          .required()
          .oneOf(ClientData.deliveryCountries?.map(c => c[1]) || [], "Invalid country"),
      }),
    deliveryPhone: yup
      .string()
      .nullable()
      .when("differentDeliveryAddress", {
        is: true,
        then: yup
          .string()
          .required()
          .matches(Util.validations.phone, "customer.phone_format")
          .when("deliveryCountry", {
            is: country => country === "NO",
            then: yup.string().nullable().length(8, "customer.phone_invalid"),
          }),
      }),
    clientRole: yup.mixed<ClientRole>().required(),
  })
}

interface EinvoiceInputProps {
  business: boolean
}

export const EinvoiceInput: React.FC<EinvoiceInputProps> = ({ business }) => {
  const { t } = useTranslation()
  const { watch } = useFormContext<CustomerFormInputs>()
  const country = watch("country")

  if (!business || !einvoiceEntry(business, country)) {
    return null
  }

  return (
    <>
      <FormInput name="orgId" id="customer_org_id" label={t("customer.org_id")} groupId="f_customer_org_id" />
      <FormInput
        name="glnNumber"
        id="customer_gln_number"
        label={t("customer.gln_number")}
        groupId="f_customer_gln_number"
      />
    </>
  )
}

interface HouseNrInputProps {
  country: string
  delivery?: boolean
  readOnly?: boolean
  onFocus?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => void
}

const HouseNrInput: React.FC<HouseNrInputProps> = ({ country, delivery, onFocus, readOnly, onKeyDown }) => {
  const { t } = useTranslation()
  const label = `${streetNrRequired(country) ? "* " : ""}${t("address.house_number")}`

  return (
    <div className="row">
      <div className="col-xs-4">
        <FormInput
          name={delivery ? "deliveryHouseNumber" : "houseNumber"}
          id={delivery ? "customer_delivery_house_number" : "customer_house_number"}
          label={label}
          autoComplete="off"
          type="search"
          readOnly={readOnly}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  )
}

interface CustomerFormProps {
  customer?: string
  customerData?: Pick<Customer, "business" | "newsletterEnabled" | "marketingConsents"> | null
}

const CustomerForm: React.FC<CustomerFormProps> = ({ customer, customerData }) => {
  const { t } = useTranslation()
  const {
    setValue,
    watch,
    getValues,
    formState: { errors },
    setError,
  } = useFormContext<CustomerFormInputs>()
  const country = watch("country")
  const deliveryCountry = watch("deliveryCountry") || ""
  const differentDelivery = watch("differentDeliveryAddress")
  const [showInvoiceEmail, setShowInvoiceEmail] = useState(false)
  const clientRole = watch("clientRole")
  const business = clientRole == ClientRole.business
  const disabledByClientRole = clientRole === null

  // copy invoice to delivery when checking different delivery
  useEffect(() => {
    if (differentDelivery && !getValues("deliveryFirstName")) {
      setValue("deliveryFirstName", getValues("firstName"))
      setValue("deliveryLastName", getValues("lastName"))
      setValue("deliveryCompany", getValues("company") || "")
      setValue("deliveryStreet", getValues("street"))
      setValue("deliveryStreet2", getValues("street2") || "")
      setValue("deliveryHouseNumber", getValues("houseNumber") || "")
      setValue("deliveryZipcode", getValues("zipcode"))
      setValue("deliveryCity", getValues("city"))
      setValue("deliveryState", getValues("state") || "")
      setValue("deliveryCountry", getValues("country"))
      setValue("deliveryPhone", getValues("phone1"))
    }
  }, [setValue, getValues, differentDelivery])

  const handleEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (!disabledByClientRole) {
        return
      }

      if (e.key === "Enter") {
        setError("clientRole", {})
      }
    },
    [disabledByClientRole, setError]
  )

  const handleFieldInteraction = useCallback(
    (e?: React.MouseEvent<HTMLSelectElement | HTMLInputElement>) => {
      if (!disabledByClientRole) {
        return
      }

      e?.preventDefault()

      setError("clientRole", {})
    },
    [disabledByClientRole, setError]
  )

  return (
    <>
      <div id="invoice_address">
        <ClientRoleSwitch clientRole={clientRole} error={!!errors.clientRole} />

        <p id="top_address_title" className="paragraph-sm">
          {differentDelivery ? t("address.invoice") : t("address.address")}
        </p>

        {business && (
          <>
            {!customer && (
              <FormInput
                name="email"
                id="customer_email"
                type="email"
                label={`* ${t("customer.email_address")}`}
                placeholder={t("address.email_placeholder")}
              />
            )}

            <FormInput name="company" id="customer_company" label={t("address.company")} groupId="f_customer_company" />

            {vatEntry(country) && (
              <FormInput
                name="vatId"
                id="customer_vat_id"
                label={t("customer.vat_id")}
                groupId="f_customer_vat_id"
                errorInterpolation={{
                  vat_code: Util.validations.vat_number_example[country],
                }}
              >
                <p className="paragraph-2xs bg-info">
                  <Trans
                    i18nKey={"address.vat_id_tooltip"}
                    components={{ a: <a href={ClientData.shippingAndPaymentPath}>read more</a> }}
                  />
                </p>
              </FormInput>
            )}

            <FormInput
              name="reference"
              id="customer_reference"
              label={`${t("address.reference_1")} (${t("address.reference_2")})`}
              groupId="f_customer_reference"
            />
          </>
        )}

        <FormInput
          name="firstName"
          id="customer_first_name"
          label={`* ${t("address.first_name")}`}
          onFocus={() => handleFieldInteraction()}
          onKeyDown={e => handleEnter(e)}
          readOnly={disabledByClientRole}
        />
        <FormInput
          name="lastName"
          id="customer_last_name"
          label={`* ${t("address.surname")}`}
          onFocus={() => handleFieldInteraction()}
          onKeyDown={e => handleEnter(e)}
          readOnly={disabledByClientRole}
        />

        <FormInput
          name="phone1"
          id="customer_phone1"
          label={differentDelivery ? t("address.phone") : `* ${t("address.phone")}`}
          placeholder={differentDelivery ? "" : t("address.phone_placeholder")}
          groupId="customer_phone1_group"
          onFocus={() => handleFieldInteraction()}
          onKeyDown={e => handleEnter(e)}
          readOnly={disabledByClientRole}
        />

        {!customer && (
          <>
            {!business && (
              <FormInput
                name="email"
                id="customer_email"
                type="email"
                label={`* ${t("customer.email_address")}`}
                placeholder={t("address.email_placeholder")}
                onFocus={() => handleFieldInteraction()}
                onKeyDown={e => handleEnter(e)}
                readOnly={disabledByClientRole}
              />
            )}
            <div className="relative">
              <div className="checkbox">
                <input
                  id="customer_different_invoice_email"
                  type="checkbox"
                  className="form-control"
                  checked={showInvoiceEmail}
                  onChange={() => setShowInvoiceEmail(!showInvoiceEmail)}
                  disabled={disabledByClientRole}
                />
                <label htmlFor="customer_different_invoice_email" className="checkbox-label paragraph-xs">
                  {t("address.different_invoice_email")}
                </label>
              </div>
            </div>
          </>
        )}

        {(showInvoiceEmail || customer) && (
          <FormInput name="invoiceEmail" id="customer_invoice_email" type="email" label={t("customer.invoice_email")} />
        )}

        {!customer && (
          <FormInput
            name="password"
            id="customer_temp_password"
            type="password"
            autoComplete="off"
            label={`* ${t("customer.new_password")}`}
            onFocus={() => handleFieldInteraction()}
            onKeyDown={e => handleEnter(e)}
            readOnly={disabledByClientRole}
          />
        )}

        <EinvoiceInput business={business} />

        <div id="customer-address">
          <FormInput
            name="street"
            id="customer_street"
            label={`* ${t("address.row_1")}`}
            onFocus={() => handleFieldInteraction()}
            onKeyDown={e => handleEnter(e)}
            readOnly={disabledByClientRole}
          />

          {streetNrRequired(country) && (
            <HouseNrInput
              onFocus={() => handleFieldInteraction()}
              onKeyDown={e => handleEnter(e)}
              readOnly={disabledByClientRole}
              country={country}
            />
          )}

          {showStreet2(country) && (
            <FormInput
              name="street2"
              id="customer_street2"
              label={streetNrRequired(country) ? t("address.row_2") : ""}
              groupId="customer_street2_group"
              placeholder={t("address.apartment_number_placeholder")}
              onFocus={() => handleFieldInteraction()}
              onKeyDown={e => handleEnter(e)}
              readOnly={disabledByClientRole}
            />
          )}

          <div className="row">
            <div className="col-xs-4">
              <FormInput
                name="zipcode"
                id="customer_zipcode"
                label={`* ${t("address.post_code")}`}
                groupId="customer_zip"
                onFocus={() => handleFieldInteraction()}
                onKeyDown={e => handleEnter(e)}
                readOnly={disabledByClientRole}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-xs-8">
              <FormInput
                name="city"
                id="customer_city"
                label={`* ${t("address.city")}`}
                onFocus={() => handleFieldInteraction()}
                onKeyDown={e => handleEnter(e)}
                readOnly={disabledByClientRole}
              />
            </div>
          </div>

          <FormSelect
            name="country"
            id="customer_country"
            label={`* ${t("address.country")}`}
            values={ClientData.deliveryCountries}
            onMouseDown={e => handleFieldInteraction(e)}
            onKeyDown={e => handleEnter(e)}
          />

          {stateRequired(country) && (
            <FormSelect
              name="state"
              id="customer_state"
              label={`* ${t("address.state")}`}
              values={country === "US" ? ClientData.deliveryStates : ClientData.deliveryStatesAU}
              onMouseDown={e => handleFieldInteraction(e)}
              onKeyDown={e => handleEnter(e)}
            />
          )}
        </div>

        <FormCheckbox
          name="differentDeliveryAddress"
          id="customer_different_delivery_address"
          label={t("address.different_delivery")}
          disabled={disabledByClientRole}
        />
        {customer && <NewsletterCheckbox customer={customerData} />}
      </div>
      <div className="right-column">
        {differentDelivery && (
          <div id="delivery_address">
            <p className="paragraph-sm">{t("address.delivery")}</p>
            {!business && (
              <>
                <FormInput
                  name="deliveryFirstName"
                  id="customer_delivery_first_name"
                  label={`* ${t("address.first_name")}`}
                />
                <FormInput
                  name="deliveryLastName"
                  id="customer_delivery_last_name"
                  label={`* ${t("address.surname")}`}
                />
              </>
            )}

            <FormInput
              name="deliveryCompany"
              id="customer_delivery_company"
              label={t("address.company")}
              groupId="f_customer_company"
            />

            {business && (
              <>
                <FormInput
                  name="deliveryFirstName"
                  id="customer_delivery_first_name"
                  label={`* ${t("address.first_name")}`}
                />
                <FormInput
                  name="deliveryLastName"
                  id="customer_delivery_last_name"
                  label={`* ${t("address.surname")}`}
                />
              </>
            )}

            <FormInput
              name="deliveryPhone"
              id="customer_delivery_phone"
              label={`* ${t("address.phone")}`}
              placeholder={t("address.phone_placeholder")}
              groupId="customer_phone1_group"
            />

            <div id="customer-address-delivery">
              <FormInput name="deliveryStreet" id="customer_delivery_street" label={`* ${t("address.row_1")}`} />

              {streetNrRequired(deliveryCountry) && <HouseNrInput country={deliveryCountry} delivery />}

              {showStreet2(deliveryCountry) && (
                <FormInput
                  name="deliveryStreet2"
                  id="customer_delivery_street2"
                  label={streetNrRequired(deliveryCountry) ? t("address.row_2") : ""}
                  groupId="customer_delivery_street2_group"
                  placeholder={t("address.apartment_number_placeholder")}
                />
              )}

              <div className="row">
                <div className="col-xs-4">
                  <FormInput
                    name="deliveryZipcode"
                    id="customer_delivery_zipcode"
                    label={`* ${t("address.post_code")}`}
                    groupId="customer_delivery_zip"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-xs-8">
                  <FormInput name="deliveryCity" id="customer_delivery_city" label={`* ${t("address.city")}`} />
                </div>
              </div>

              <FormSelect
                name="deliveryCountry"
                id="customer_delivery_country"
                label={`* ${t("address.country")}`}
                values={ClientData.deliveryCountries}
              />

              {stateRequired(deliveryCountry) && (
                <FormSelect
                  name="deliveryState"
                  id="customer_delivery_state"
                  label={`* ${t("address.state")}`}
                  values={deliveryCountry === "US" ? ClientData.deliveryStates : ClientData.deliveryStatesAU}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CustomerForm
